/** Browser diagnostics emitted when ResizeObserver defers a delivery are not
 * script failures. A real exception carrying the same message must remain
 * visible, so callers must check the ErrorEvent error value too. */
export function isBenignResizeObserverDiagnostic(event: { error?: unknown; message?: unknown }) {
  return event.error == null && (event.message === 'ResizeObserver loop completed with undelivered notifications.'
    || event.message === 'ResizeObserver loop limit exceeded');
}

/** Executed only inside the opaque transcript iframe. Keep this function closed:
 * it is serialized into srcdoc, and cannot depend on module scope. */
export function installWebHost(configuration: { token: string; parentOrigin: string; presentation: Record<string, unknown>; themePresentations?: Record<'light' | 'dark', Record<string, unknown>>; document?: boolean;plotRuntimeUrl?:string;readOnly?:boolean }) {
  const w = window as any;
  const token = configuration.token;
  let sequence = 0, booted = false, configurationDIL: any = null;
  let theme: 'light' | 'dark' = configuration.presentation.theme === 'dark' ? 'dark' : 'light';
  let activeDILConfiguration: any = null;
  let parseBilibiliReference: ((href: string) => { bvid: string; startSeconds?: number; endSeconds?: number; pageNumber: number;
    videoTimeLocations?: Array<{ startSeconds: number; endSeconds?: number }> } | null) | undefined;
  const bilibiliMetadataPending = new Map<string, Promise<any>>();
  const pending = new Map<string, (value: any) => void>();
  const referenceCache = new Map<string, any>();
  const mediaReferences = new Map<string, any>();
  const mediaState = new Map<string, { time: number; rate: number }>();
  const mediaRetried = new WeakSet<HTMLImageElement | HTMLVideoElement>();
  const videoPlayback = new WeakMap<HTMLVideoElement, { playing: boolean; start?: number; end?: number }>();
  const videoDisposers = new Map<HTMLVideoElement, () => void>();
  const pointFrames = new Map<HTMLElement, { load: () => void; controller: AbortController | null }>();
  let pointObserver: IntersectionObserver | null = null, resourceVersion: string | undefined;
  let lastPayload: any = null;
  let transcriptScope: string | null = null, renderSequence = 0, lastScrollRequest = -1;
  let hasCommittedRender = false, initialScrollOwnershipGeneration = 0;
  let deferredRender: { data: any; firstRender: boolean; sequence: number } | null = null;
  let renderRetryTimer: ReturnType<typeof setTimeout> | null = null;
  let queuedRender: { data: any; firstRender: boolean; sequence: number } | null = null, renderBurstOpen = false;
  // History replay posts one complete payload per parent commit, far faster than
  // a full transcript render. A render opens a burst that closes once the posts
  // already queued behind it are drained; payloads arriving inside the burst
  // supersede each other, so the burst ends with one render of the newest.
  // Hosts without MessageChannel render each payload on arrival.
  const renderBurst = typeof MessageChannel === 'function' ? new MessageChannel() : null;
  if (renderBurst) renderBurst.port1.onmessage = () => closeRenderBurst();
  let turnNavigationScope = '', turnNavigationSequence = 0;
  let visible = true, mediaVisibilityGeneration = 0;
  let forkControls = { enabled: false, disabled: false, messageID: null as string | null };
  function applyTheme(value: 'light' | 'dark') {
    if (theme === value) return;
    theme = value;
    w.__readosWebVisualizations?.theme(value);
    const presentation = { ...w.__chatTranscriptPresentation, ...configuration.themePresentations?.[theme], theme };
    const previousSkip = w.__chatTranscriptSkipPresentationRerender;
    w.__chatTranscriptSkipPresentationRerender = true;
    try {
      w.__chatTranscriptPresentationController?.setConversationPresentation(presentation);
    } finally { w.__chatTranscriptSkipPresentationRerender = previousSkip; }
    // Configure only appearance and the current admission/scope. Replaying the
    // original saved state here would overwrite later interactive DIL input.
    if (activeDILConfiguration) {
      activeDILConfiguration = { ...activeDILConfiguration, theme, visible };
      w.ReadOSIntelligentUI?.configure(activeDILConfiguration);
    }
  }
  function applyVisibility(value: boolean) {
    visible = value;
    w.__readosWebVisualizations?.visible(value);
    w.ReadOSIntelligentUI?.setVisible(value);
    if (!value) { mediaVisibilityGeneration++; document.querySelectorAll<HTMLMediaElement>('video,audio').forEach(media => media.pause()); }
    for (const [frame, point] of pointFrames) {
      if (!value) point.controller?.abort();
      else if (frame.isConnected) { pointObserver?.unobserve(frame); pointObserver?.observe(frame); }
    }
  }
  const send = (type: string, value: any = {}) => {
    if (configuration.readOnly && ['followUp','selectionAction','openAgent','fork','asyncQuestionAnswer','state'].includes(type)) return;
    const key = type === 'navigate' ? 'href' : type === 'openUrl' ? 'url' : null;
    const output = key && typeof value[key] === 'string' ? { ...value, [key]: browserNavigationHref(value[key]) } : value;
    parent.postMessage({ channel: 'reados-renderer-v1', token, type, ...output }, configuration.parentOrigin);
  };
  function scheduleRenderRetry(data: any, firstRender: boolean, currentRenderSequence: number) {
    if (currentRenderSequence !== renderSequence) return;
    // A complete render payload supersedes earlier streaming snapshots. Keep
    // exactly one pending publication, with no success ACK until DOM commit.
    deferredRender = { data, firstRender, sequence: currentRenderSequence };
    if (renderRetryTimer !== null) return;
    renderRetryTimer = setTimeout(() => {
      renderRetryTimer = null;
      const pending = deferredRender;
      if (!pending || pending.sequence !== renderSequence) return;
      const state = w.__chatTranscriptUIState;
      if (!visible || state?.userScrollContactActive === true || state?.platformScrollOwnershipActive === true
        || w.__chatTranscriptScrollCoordinator?.hasRecentUserScrollGesture?.()
        || w.__chatTranscriptScrollCoordinator?.isLifecycleSuspended?.() === true
        || window.innerHeight === 0) {
        scheduleRenderRetry(pending.data, pending.firstRender, pending.sequence);
        return;
      }
      deferredRender = null;
      void renderPayload(pending.data, pending.firstRender, pending.sequence);
    }, 160);
  }
  function openRenderBurst() {
    if (!renderBurst) return;
    renderBurstOpen = true;
    renderBurst.port2.postMessage(null);
  }
  function closeRenderBurst() {
    renderBurstOpen = false;
    flushQueuedRender();
  }
  function flushQueuedRender() {
    const next = queuedRender;
    queuedRender = null;
    if (!next || next.sequence !== renderSequence) return;
    openRenderBurst();
    void renderPayload(next.data, next.firstRender && !hasCommittedRender, next.sequence);
  }
  const request = (kind: string, value: any, signal?: AbortSignal): Promise<any> => new Promise(resolve => {
    const id = `r${++sequence}`;
    const finish = (result: any) => { clearTimeout(timer); signal?.removeEventListener('abort', abort); resolve(result); };
    const abort = () => { if (pending.delete(id)) { send('cancelRequest', { id }); finish(null); } };
    const timer = setTimeout(() => { if (pending.delete(id)) { if (kind === 'videoFrame') send('cancelRequest', { id }); finish(null); } }, 15000);
    if (signal?.aborted) { finish(null); return; }
    pending.set(id, finish);
    signal?.addEventListener('abort', abort, { once: true });
    send('request', { id, kind, value });
  });
  w.__readosPlotRuntimeURL=()=>configuration.plotRuntimeUrl||'';
  w.__readosLoadPlotFigure=async(source:string,signal?:AbortSignal)=>{
    const url=new URL(source);
    if(url.protocol!=='reados-plot:'||url.hostname!=='figure'||url.username||url.password||url.hash||url.searchParams.size!==2)throw new Error('图表身份无效');
    const value=await request('plotFigure',{receiptId:url.searchParams.get('receipt'),outputId:url.searchParams.get('output')},signal);
    if(!value)throw new Error('图表已改变或不可用');return value;
  };
  function notice(message: string) {
    let el = document.getElementById('web-host-notice');
    if (!el) { el = document.createElement('div'); el.id = 'web-host-notice'; el.setAttribute('role', 'status'); document.body.append(el); }
    el.textContent = message;
    setTimeout(() => el?.remove(), 5000);
  }
  let outlinePreview: { dialog: HTMLDialogElement; messageId: string; attachmentId: string; markdown: string } | null = null;
  function showOutlinePreview(message: any, attachment: any) {
    if(attachment.kind!=='pdf_outline_context'||typeof attachment.markdown!=='string'||!attachment.markdown)return;
    outlinePreview?.dialog.close();
    const focus=document.activeElement instanceof HTMLElement?document.activeElement:null;
    const dialog=document.createElement('dialog');dialog.className='reados-outline-preview';
    dialog.setAttribute('aria-label','PDF 目录附件预览');
    const header=document.createElement('header'),heading=document.createElement('h2');
    heading.textContent=attachment.displayName||'PDF 目录';
    const close=document.createElement('button');close.type='button';close.textContent='关闭';close.setAttribute('aria-label','关闭 PDF 目录附件预览');
    close.onclick=()=>dialog.close();header.append(heading,close);
    const content=document.createElement('div');content.className='reados-outline-preview-content';
    dialog.append(header,content);document.body.append(dialog);
    outlinePreview={dialog,messageId:message.id,attachmentId:attachment.id,markdown:attachment.markdown};
    dialog.addEventListener('close',()=>{
      if(outlinePreview?.dialog===dialog)outlinePreview=null;
      dialog.remove();if(focus?.isConnected)focus.focus({preventScroll:true});
    },{once:true});
    // Reuse the copied iPad Markdown renderer inside the existing opaque
    // sandbox. Do not interpret this metadata as DIL or send it to a model.
    try {
      w.ChatMarkdownRenderer.renderMarkdownInto(content,attachment.markdown,{streaming:false});
      dialog.showModal();close.focus({preventScroll:true});
    } catch { dialog.remove();outlinePreview=null;notice('目录附件预览暂时不可用'); }
  }
  function safeMediaURL(raw: unknown): string {
    if (typeof raw !== 'string') return '';
    try {
      const u = new URL(raw);
      if (u.origin !== configuration.parentOrigin) return '';
      const allowed = configuration.readOnly ? /^\/api\/admin\/inspection-media\/[A-Za-z0-9_-]+$/ : /^\/api\/media\/[A-Za-z0-9_-]+$/;
      return allowed.test(u.pathname) && !u.username && !u.password && !u.search && !u.hash ? u.href : '';
    } catch { return ''; }
  }
  function workspaceImageSource(raw: unknown): string {
    if (typeof raw !== 'string' || raw.length > 8192 || !raw.startsWith('/workspace/')
      || /[\u0000-\u001f\u007f\\]/.test(raw) || raw.slice(1).split('/').some(part => !part || part === '.' || part === '..')) return '';
    const url = new URL('/api/artifacts/content', configuration.parentOrigin);
    url.searchParams.set('path', raw); return url.href;
  }
  function attachmentImageSource(raw: unknown): string {
    if (typeof raw !== 'string' || raw.length > 8192) return '';
    try {
      const url = new URL(raw, configuration.parentOrigin);
      return url.origin === configuration.parentOrigin && !url.username && !url.password && !url.hash
        && /^\/api\/files\/[a-f0-9-]{36}\/content$/.test(url.pathname) && url.searchParams.size === 1
        && /^[a-f0-9]{64}$/.test(url.searchParams.get('contentRevision') || '') ? url.href : '';
    } catch { return ''; }
  }
  // The copied preview renderer needs a recognizable source before it creates
  // its image card. This is a deferred identity, never an iframe network URL.
  w.__readosWorkspaceImageSource = workspaceImageSource;
  let imageViewerReturn: { requestId: string; focus: HTMLElement | null; x: number; y: number;
    scroll: Array<{ node: HTMLElement; left: number; top: number }> } | null = null;
  function imageSource(raw: unknown): string {
    if (typeof raw !== 'string') return '';
    if (workspaceImageSource(raw)) return raw;
    const attachment = attachmentImageSource(raw); if (attachment) return attachment;
    if (configuration.document && raw && raw.length <= 8192 && !/^[a-z][a-z0-9+.-]*:/i.test(raw)
      && !raw.startsWith('//') && !/[\u0000-\u001f\u007f\\]/.test(raw)) return raw;
    // Inline raster previews remain self-contained; SVG and arbitrary remote
    // URLs never cross into the trusted parent viewer.
    if (raw.length <= 1_000_000 && /^data:image\/(png|jpeg|webp|gif);base64,[A-Za-z0-9+/=\s]+$/.test(raw)) return raw;
    const ticket = safeMediaURL(raw);
    if (ticket) {
      const reference = mediaReferences.get(ticket);
      if (attachmentImageSource(reference?.sourceIdentity)) return reference.sourceIdentity;
      const path = reference?.target?.path;
      return workspaceImageSource(path) ? path : ticket;
    }
    try {
      const url = new URL(raw);
      const path = url.searchParams.get('path');
      if (url.origin === configuration.parentOrigin && url.pathname === '/api/artifacts/content'
        && url.searchParams.size === 1 && !url.hash && workspaceImageSource(path) === url.href) return path!;
    } catch { /* Unrecognized image identities are not viewer capabilities. */ }
    return '';
  }
  if (configuration.document) w.__readosDocumentImageDestination = (source: string) => {
    // This hook only admits an unchanged destination for parent resolution;
    // it never returns a network URL or grants a media capability itself.
    return source && source.length <= 8192 && !source.startsWith('/') && !source.startsWith('#')
      && !/^[a-z][a-z0-9+.-]*:/i.test(source) && !/[\u0000-\u001f\u007f\\]/.test(source) ? source : '';
  };
  function previewImageSource(preview: any): string {
    const payload = preview?.payload || {};
    // A thumbnail is only an entry point. Open the original artifact whenever
    // its authoritative path is available, instead of enlarging thumbnail pixels.
    for (const candidate of [payload.output_path, payload.outputPath, payload.image_path, payload.imagePath,
      payload.path, payload.file_path, payload.filePath, preview?.filePath, preview?.file_path,
      payload.image_data_url, payload.imageDataURL, payload.imageDataUrl, payload.image_url, payload.imageURL,
      payload.preview_url, payload.previewURL, payload.thumbnail_data_url, payload.thumbnailDataURL]) {
      const source = imageSource(candidate); if (source) return source;
    }
    return '';
  }
  function sourceForImage(image: HTMLImageElement): string {
    const card = image.closest('.readex-image-view-preview') as any;
    return previewImageSource(card?.__readexImageViewState?.preview)
      || imageSource(image.dataset.webImageSource) || imageSource((image as any).__readosDilProps?.src)
      || imageSource(image.getAttribute('src'));
  }
  function showImage(raw: string, title = '图片预览', clickedImage?: HTMLImageElement | null) {
    const source = imageSource(raw); if (!source) return;
    const selected = clickedImage || Array.from(document.querySelectorAll<HTMLImageElement>('#messages img'))
      .find(image => sourceForImage(image) === source) || null;
    const group = selected?.closest('.reados-web-video-gallery, .message-attachments, .readex-image-view-preview-list, .readex-image-generation-preview-list')
      || selected?.closest('article.message');
    const candidates = group ? Array.from(group.querySelectorAll<HTMLImageElement>('img')) : selected ? [selected] : [];
    const items: Array<{ source: string; title: string }> = [];
    let otherSourceLength = 0;
    for (const image of candidates) {
      const itemSource = sourceForImage(image);
      if (!itemSource || items.some(item => item.source === itemSource)) continue;
      // Keep a bounded bridge message even when several inline previews exist.
      if (itemSource !== source && otherSourceLength + itemSource.length > 1_500_000 - source.length) continue;
      items.push({ source: itemSource, title: (image.alt || '图片预览').slice(0, 1000) });
      if (itemSource !== source) otherSourceLength += itemSource.length;
      if (items.length === 63) break;
    }
    let index = items.findIndex(item => item.source === source);
    if (index < 0) { items.unshift({ source, title: String(title || '图片预览').slice(0, 1000) }); index = 0; }
    const focus = selected?.closest<HTMLElement>('button') || (document.activeElement instanceof HTMLElement ? document.activeElement : null);
    const scroll: Array<{ node: HTMLElement; left: number; top: number }> = [];
    for (let node = selected?.parentElement; node; node = node.parentElement) {
      if (node.scrollHeight > node.clientHeight || node.scrollWidth > node.clientWidth)
        scroll.push({ node, left: node.scrollLeft, top: node.scrollTop });
    }
    const requestId = `image-${++sequence}`;
    imageViewerReturn = { requestId, focus, x: window.scrollX, y: window.scrollY, scroll };
    // The viewer lives in the parent document, so it can fill the entire Web
    // window even when this opaque iframe occupies a narrow chat pane.
    send('openImages', { items, index, requestId });
  }
  async function resolve(href: string) {
    if (!referenceCache.has(href)) referenceCache.set(href, request('resolve', { href }));
    let result = await referenceCache.get(href);
    if(result?.expiresAt && result.expiresAt <= Date.now()+30_000) {
      referenceCache.set(href,request('resolve',{href})); result = await referenceCache.get(href);
    }
    return result;
  }
  function referenceLinkURL(href: string): URL | null {
    if (!href || href.length > 8192 || href.trim().startsWith('//') || /[\u0000-\u001f\u007f\\]/.test(href)) return null;
    try {
      const url = new URL(href, configuration.parentOrigin);
      return ['http:', 'https:', 'readex:'].includes(url.protocol) && !url.username && !url.password ? url : null;
    } catch { return null; }
  }
  function externalReferenceURL(url: URL | null): url is URL {
    return !!url && ['http:', 'https:'].includes(url.protocol) && url.origin !== configuration.parentOrigin;
  }
  function browserNavigationHref(href: string): string {
    const url = referenceLinkURL(href);
    if (!externalReferenceURL(url)) return href;
    const reference = parseBilibiliReference?.(href);
    if (!reference || !Number.isFinite(reference.startSeconds) || reference.startSeconds! < 0) return href;
    // ReadOS's fragment keeps the cited range; Bilibili's website seeks using
    // a numeric query parameter. Derive only the outbound browser URL.
    url.searchParams.set('t', String(reference.startSeconds));
    if (reference.pageNumber > 1) url.searchParams.set('p', String(reference.pageNumber));
    return url.href;
  }
  function referencePayload(reference: any) {
    const t = reference.target;
    return { action: 'openReadexContentReference', path: t.path, contentID: t.fileId, pageLabel: t.pageLabel,
      pageNumber: t.pageNumber, pageLocations: t.pageLocations, startSeconds: t.start, endSeconds: t.end,
      lineNumber: t.lineStart, endLineNumber: t.lineEnd,
      videoTimeLocations: t.timeLocations?.map((p: any) => ({ startSeconds: p.start, endSeconds: p.end })) };
  }
  function videoSourceButton(reference: any, start: number, end?: number, timeOnly = false) {
    const caption = document.createElement('button'); caption.type = 'button'; caption.className = 'reados-web-video-source';
    const time = `${formatTime(start)}${end === undefined ? '' : '–' + formatTime(end)}`;
    const label = `${reference.label} · ${time}`;
    caption.textContent = timeOnly ? time : label; caption.setAttribute('aria-label', label);
    caption.onclick = () => send('navigate', { href: '', payload: { ...referencePayload(reference), startSeconds: start, endSeconds: end,
      videoTimeLocations: [{ startSeconds: start, endSeconds: end }] } });
    return caption;
  }
  function disposePointFrame(frame: HTMLElement) {
    pointFrames.get(frame)?.controller?.abort(); pointFrames.delete(frame); pointObserver?.unobserve(frame);
  }
  function mountPointFrame(container: HTMLElement, href: string, reference: any, time: number) {
    const key = `${reference.target.path}:${reference.contentRevision || ''}:${time}:time-only`;
    if (container.dataset.webVideoKey === key && pointFrames.has(container)) return;
    disposePointFrame(container); container.querySelectorAll('video').forEach(disposeVideo);
    container.dataset.webVideoKey = key; container.classList.remove('reados-web-video'); container.classList.add('reados-web-point-frame');
    const button = document.createElement('button'); button.type = 'button'; button.className = 'reados-web-video-frame-image';
    button.setAttribute('aria-label', `放大 ${formatTime(time)} 视频帧`);
    const image = document.createElement('img'); image.alt = `${reference.label} · ${formatTime(time)}`; image.decoding = 'async';
    const status = document.createElement('span'); status.className = 'reados-web-video-frame-status'; status.setAttribute('role', 'status'); status.textContent = '正在读取视频帧…';
    button.append(image, status); container.replaceChildren(button, videoSourceButton(reference, time, undefined, true));
    const point = { controller: null as AbortController | null, load: () => {} };
    point.load = () => {
      if (!visible || !container.isConnected || point.controller || image.getAttribute('src')) return;
      const controller = new AbortController(); point.controller = controller;
      button.disabled = true; status.hidden = false; status.textContent = '正在读取视频帧…';
      void request('videoFrame', { href, time }, controller.signal).then(result => {
        if (controller.signal.aborted || !container.isConnected || pointFrames.get(container) !== point) return;
        const source = result?.source;
        if (typeof source === 'string' && source.length < 1_000_000 && /^data:image\/jpeg;base64,[A-Za-z0-9+/=]+$/.test(source)) {
          image.src = source; image.dataset.webImageSource = source; status.hidden = true;
        } else status.textContent = '视频帧暂时不可用，点击重试';
      }).finally(() => { if (point.controller === controller) { point.controller = null; button.disabled = false; } });
    };
    button.onclick = () => image.getAttribute('src') ? showImage(image.src, image.alt, image) : point.load();
    pointFrames.set(container, point);
    pointObserver ??= new IntersectionObserver(entries => {
      for (const entry of entries) if (entry.isIntersecting) pointFrames.get(entry.target as HTMLElement)?.load();
    }, { rootMargin: '160px 0px' });
    pointObserver.observe(container);
  }
  function mountVideo(container: HTMLElement, reference: any, start: number, end?: number) {
    const src = safeMediaURL(reference.resourceUrl); if (!src) return;
    const key = `${reference.target.path}:${reference.contentRevision || ''}:${start}:${end ?? ''}`;
    if (container.dataset.webVideoKey === key && container.querySelector('video')) return;
    container.dataset.webVideoKey = key;
    disposePointFrame(container); container.classList.remove('reados-web-point-frame');
    container.classList.add('reados-web-video');
    container.querySelectorAll('video').forEach(disposeVideo);
    const video = document.createElement('video'); video.preload = 'metadata'; video.playsInline = true;
    const events = new AbortController(), signal = events.signal;
    video.setAttribute('aria-label', `${reference.label} · ${start}${end === undefined ? '' : '–' + end} 秒`);
    const bounded = end !== undefined && end > start;
    video.controls = !bounded;
    videoPlayback.set(video, { playing: false, end: bounded ? end : undefined });
    const bounds = () => {
      const duration = Number.isFinite(video.duration) ? Math.max(0, video.duration) : Infinity;
      return { start: Math.min(start, duration), end: Math.min(end ?? duration, duration) };
    };
    const clamp = (time: number) => bounded ? Math.min(bounds().end, Math.max(bounds().start, time))
      : Math.min(time, Number.isFinite(video.duration) ? Math.max(video.duration - 0.01, 0) : start);
    video.addEventListener('loadedmetadata', () => {
      const saved = mediaState.get(key);
      video.currentTime = clamp(saved?.time ?? start);
      video.playbackRate = saved?.rate ?? 1;
      if (bounded) videoPlayback.set(video, { ...videoPlayback.get(video)!, start: bounds().start, end: bounds().end });
    }, { signal });
    video.addEventListener('timeupdate', () => {
      if (bounded && video.currentTime >= bounds().end) { video.pause(); if (video.currentTime > bounds().end) video.currentTime = bounds().end; }
      if (video.isConnected) mediaState.set(key, { time: video.currentTime, rate: video.playbackRate });
    }, { signal });
    video.addEventListener('seeking', () => {
      if (bounded && video.currentTime !== clamp(video.currentTime)) video.currentTime = clamp(video.currentTime);
    }, { signal });
    video.addEventListener('play', () => { if (bounded && video.currentTime >= bounds().end) video.currentTime = bounds().start; }, { signal });
    video.src = src;
    const caption = videoSourceButton(reference, start, end, bounded);
    if (bounded) {
      const player = document.createElement('div'); player.className = 'reados-web-range-player';
      player.setAttribute('role', 'group'); player.setAttribute('aria-label', `视频片段 ${formatTime(start)}–${formatTime(end!)}`);
      player.append(video);
      installRangeVideoControls(player, video, bounds);
      container.replaceChildren(player, caption);
    } else container.replaceChildren(video, caption);
    const disposeControls = videoDisposers.get(video);
    videoDisposers.set(video, () => {
      mediaState.set(key, { time: clamp(video.currentTime), rate: video.playbackRate }); events.abort(); disposeControls?.();
    });
  }
  function disposeVideo(video: HTMLVideoElement) {
    video.pause();
    videoDisposers.get(video)?.();
    videoDisposers.delete(video);
    // Detached players must release their decoder and capability-backed request.
    video.removeAttribute('src'); video.load();
  }
  function installRangeVideoControls(player: HTMLElement, video: HTMLVideoElement, bounds: () => { start: number; end: number }) {
    const events = new AbortController(), signal = events.signal;
    let animation = 0;
    const controls = document.createElement('div'); controls.className = 'reados-web-range-controls';
    const seek = document.createElement('input'); seek.type = 'range'; seek.step = '0.001'; seek.className = 'reados-web-range-seek';
    seek.setAttribute('aria-label', '片段播放进度');
    const row = document.createElement('div'); row.className = 'reados-web-range-toolbar';
    const icons: Record<string, string> = {
      play: '<path d="m8 5 11 7-11 7Z" fill="currentColor" stroke="none"/>',
      pause: '<path d="M8 5v14M16 5v14" stroke-width="4"/>',
      volume: '<path d="m11 5-5 4H3v6h3l5 4Zm4 4a4 4 0 0 1 0 6m3-9a8 8 0 0 1 0 12"/>',
      muted: '<path d="m11 5-5 4H3v6h3l5 4Zm5 4 6 6m0-6-6 6"/>',
      fullscreen: '<path d="M9 3H3v6m12-6h6v6M3 15v6h6m12-6v6h-6"/>',
    };
    function button(label: string, symbol: string) {
      const node = document.createElement('button'); node.type = 'button'; setLabel(node, label, symbol); return node;
    }
    const play = button('播放片段', 'play'), mute = button('静音', 'volume'), fullscreen = button('全屏播放片段', 'fullscreen');
    const time = document.createElement('span'); time.className = 'reados-web-range-time';
    const speed = document.createElement('select'); speed.className = 'reados-web-range-speed'; speed.setAttribute('aria-label', '播放速度');
    for (const rate of [0.5, 1, 1.25, 1.5, 2]) {
      const option = document.createElement('option'); option.value = String(rate); option.textContent = `${rate}×`; speed.append(option);
    }
    row.append(play, time, mute, speed, fullscreen); controls.append(seek, row); player.append(controls);
    // Fullscreen the wrapper, never the video: native video fullscreen would
    // reintroduce a full-source timeline and lose the selected interval.
    fullscreen.hidden = !document.fullscreenEnabled || !player.requestFullscreen;
    function setLabel(node: HTMLButtonElement, label: string, symbol?: string) {
      if (node.getAttribute('aria-label') !== label) { node.setAttribute('aria-label', label); node.title = label; }
      if (symbol && node.dataset.icon !== symbol) {
        node.dataset.icon = symbol;
        node.innerHTML = `<svg aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">${icons[symbol]}</svg>`;
      }
    }
    function update() {
      const limits = bounds(), ready = video.readyState >= 1 && limits.end > limits.start;
      const current = Math.min(limits.end, Math.max(limits.start, video.currentTime));
      seek.min = String(limits.start); seek.max = String(limits.end); seek.value = String(current);
      seek.disabled = !ready; play.disabled = !ready; speed.disabled = !ready;
      const label = `${formatTime(Math.floor(current))} / ${formatTime(limits.end)}`;
      if (time.textContent !== label) time.textContent = label;
      seek.setAttribute('aria-valuetext', label);
      setLabel(play, video.paused ? current >= limits.end ? '重播片段' : '播放片段' : '暂停片段', video.paused ? 'play' : 'pause');
      setLabel(mute, video.muted ? '取消静音' : '静音', video.muted ? 'muted' : 'volume');
      mute.setAttribute('aria-pressed', String(video.muted)); speed.value = String(video.playbackRate);
      setLabel(fullscreen, document.fullscreenElement === player ? '退出全屏' : '全屏播放片段');
    }
    function stopAnimation() { if (animation) cancelAnimationFrame(animation); animation = 0; }
    function trackPlayback() {
      stopAnimation();
      if (!video.isConnected || video.paused || !visible) return;
      const end = bounds().end;
      if (video.currentTime >= end) {
        video.pause(); if (video.currentTime !== end) video.currentTime = end;
      }
      update();
      if (!video.paused) animation = requestAnimationFrame(trackPlayback);
    }
    async function togglePlayback() {
      if (!video.paused) { video.pause(); return; }
      if (video.currentTime >= bounds().end) video.currentTime = bounds().start;
      try { await video.play(); } catch { if (video.isConnected && !video.error && visible) notice('视频暂时无法播放，请重试'); }
    }
    play.addEventListener('click', () => void togglePlayback(), { signal });
    video.addEventListener('click', () => { if (!play.disabled) void togglePlayback(); }, { signal });
    seek.addEventListener('input', () => {
      video.currentTime = Math.min(bounds().end, Math.max(bounds().start, Number(seek.value))); update();
    }, { signal });
    seek.addEventListener('keydown', event => {
      const limits = bounds(), current = Number(seek.value);
      const target = event.key === 'Home' ? limits.start : event.key === 'End' ? limits.end
        : ['ArrowRight', 'ArrowUp'].includes(event.key) ? current + 1
        : ['ArrowLeft', 'ArrowDown'].includes(event.key) ? current - 1 : undefined;
      if (target === undefined) return;
      event.preventDefault(); video.currentTime = Math.min(limits.end, Math.max(limits.start, target)); update();
    }, { signal });
    mute.addEventListener('click', () => { video.muted = !video.muted; }, { signal });
    speed.addEventListener('change', () => { video.playbackRate = Number(speed.value); }, { signal });
    fullscreen.addEventListener('click', async () => {
      try { if (document.fullscreenElement === player) await document.exitFullscreen(); else await player.requestFullscreen(); }
      catch { notice('当前浏览器无法进入全屏'); }
    }, { signal });
    document.addEventListener('fullscreenchange', update, { signal });
    for (const type of ['loadedmetadata', 'durationchange', 'timeupdate', 'seeking', 'seeked', 'ratechange', 'volumechange', 'loadeddata', 'emptied'])
      video.addEventListener(type, update, { signal });
    video.addEventListener('play', () => { update(); trackPlayback(); }, { signal });
    for (const type of ['pause', 'ended', 'error']) video.addEventListener(type, () => { stopAnimation(); update(); }, { signal });
    videoDisposers.set(video, () => { stopAnimation(); events.abort(); });
    update();
  }
  function formatTime(value: number) {
    const n = Math.floor(value), fraction = String(value).split('.')[1];
    const seconds = (n % 60).toString().padStart(2, '0') + (fraction ? '.' + fraction : '');
    const minutes = Math.floor(n / 60) % 60;
    return (n >= 3600 ? Math.floor(n / 3600) + ':' : '') + minutes.toString().padStart(2, '0') + ':' + seconds;
  }
  function bilibiliTooltip(video: NonNullable<ReturnType<NonNullable<typeof parseBilibiliReference>>>, metadata?: any) {
    const matched = metadata?.bvid === video.bvid && metadata?.pageNumber === video.pageNumber;
    const title = matched && typeof metadata.title === 'string' ? metadata.title.trim() : '';
    const partTitle = matched && typeof metadata.partTitle === 'string' ? metadata.partTitle.trim() : '';
    const locations = video.videoTimeLocations?.length ? video.videoTimeLocations
      : video.startSeconds === undefined ? [] : [{ startSeconds: video.startSeconds, endSeconds: video.endSeconds }];
    const times = locations.map(point => formatTime(point.startSeconds)
      + (point.endSeconds === undefined || point.endSeconds === point.startSeconds ? '' : '–' + formatTime(point.endSeconds))).join('、');
    return { primary: title || video.bvid,
      path: [video.pageNumber > 1 ? `P${video.pageNumber}` : '', partTitle && partTitle !== title ? partTitle : '', times].filter(Boolean).join(' · ') || 'Bilibili' };
  }
  function bilibiliMetadata(href: string, video: NonNullable<ReturnType<NonNullable<typeof parseBilibiliReference>>>) {
    if (!/^BV[A-Za-z0-9]{10}$/.test(video.bvid)) return Promise.resolve(null);
    const existing = bilibiliMetadataPending.get(href);
    if (existing) return existing;
    if (bilibiliMetadataPending.size >= 200) return Promise.resolve(null);
    const pending = request('videoMetadata', { bvid: video.bvid, pageNumber: video.pageNumber })
      .then(value => value?.bvid === video.bvid && value?.pageNumber === video.pageNumber && typeof value.title === 'string' && value.title.trim() ? value : null)
      .finally(() => bilibiliMetadataPending.delete(href));
    bilibiliMetadataPending.set(href, pending); return pending;
  }
  function applyBilibiliMetadataUpgrades(values: any[], requestID?: string) {
    const upgraded = Array.from(new Map(values.filter(Boolean).map(result => [result.href, result])).values());
    if (!upgraded.length) return;
    // Streaming can replace anchors while metadata is loading. Retain
    // exact href identity, and bind the upgrade to their current IDs.
    const current = Array.from(document.querySelectorAll<HTMLAnchorElement>('#messages a[data-readex-content-reference-validation-id]'));
    const rebound = upgraded.flatMap(result => {
      const anchors = current.filter(anchor => anchor.getAttribute('href') === result.href);
      return anchors.length ? anchors.map(anchor => ({ ...result, id: anchor.dataset.readexContentReferenceValidationId })) : [result];
    });
    w.__chatTranscriptApplyReadexContentReferenceValidation?.({ requestID, complete: true, results: rebound });
    const upgradedHrefs = new Set(upgraded.map(result => result.href));
    const active = current.find(anchor => upgradedHrefs.has(anchor.getAttribute('href')) && anchor.getAttribute('aria-describedby') === 'message-source-link-tooltip'
      && (anchor.matches(':hover') || document.activeElement === anchor));
    // Reuse the existing tooltip handler when the pointer stays still while
    // metadata upgrades its descriptor. This does not perform navigation.
    active?.dispatchEvent(new MouseEvent('mouseover', { bubbles: true }));
  }
  async function decorateAnchor(anchor: HTMLAnchorElement) {
    const href = anchor.getAttribute('href') || '';
    if (!href || anchor.dataset.webReferenceHref === href || anchor.closest('.reados-web-lightbox')) return;
    anchor.dataset.webReferenceHref = href;
    const url = referenceLinkURL(href);
    if (!url || externalReferenceURL(url)) return;
    const version = resourceVersion;
    const reference = await resolve(href);
    if (!anchor.isConnected || anchor.getAttribute('href') !== href || version !== resourceVersion) return;
    if (!reference) { w.ReadexCitationLayout?.release(anchor, 'web-video'); return; }
    anchor.dataset.webReference = 'true';
    (anchor as any).__webReference = reference;
    if (reference.kind !== 'video' || !reference.target.timeLocations?.length) return;
    // Original citation-layout owns insertion and cleanup. Point locations are
    // real stills; only actual ranges retain a live browser player.
    const media = w.ReadexCitationLayout?.acquire(anchor, 'web-video', {
      create() { const node = document.createElement('div'); node.className = 'readex-local-video-citation-media reados-web-video-gallery'; return node; },
      dispose(node: HTMLElement) { node.querySelectorAll('video').forEach(disposeVideo); for (const child of node.children) disposePointFrame(child as HTMLElement); }
    });
    if (!media) return;
    media.classList.toggle('is-multiple', reference.target.timeLocations.length > 1);
    for (const [index, location] of reference.target.timeLocations.entries()) {
      let frame = media.children[index] as HTMLElement;
      if (!frame) { frame = document.createElement('figure'); frame.className = 'readex-local-video-frame'; media.append(frame); }
      if (location.end !== undefined && location.end > location.start) mountVideo(frame, reference, location.start, location.end);
      else mountPointFrame(frame, href, reference, location.start);
    }
    while (media.children.length > reference.target.timeLocations.length) {
      const extra = media.lastElementChild as HTMLElement; disposePointFrame(extra); extra.querySelectorAll('video').forEach(disposeVideo); extra.remove();
    }
  }
  let scanScheduled = false;
  function scan() {
    if (scanScheduled) return; scanScheduled = true;
    requestAnimationFrame(() => {
      scanScheduled = false;
      w.__readosAnnotationCapsules?.refresh();
      document.querySelectorAll<HTMLAnchorElement>('#messages a[href]').forEach(anchor => void decorateAnchor(anchor));
      document.querySelectorAll<HTMLImageElement>('#messages img').forEach(image => {
        const src = image.getAttribute('src') || (image as any).__readosDilProps?.src || '';
        if (!src || image.dataset.webImageSource === src || safeMediaURL(src) || src.startsWith('data:')) return;
        image.dataset.webImageSource = src;
        void resolve(src).then(reference => {
          if (reference?.kind !== 'image' || !image.isConnected || !safeMediaURL(reference.resourceUrl)) return;
          const state=(image as any).__readosDilImageLoad;
          if(state){state.source=reference.resourceUrl;state.status='loading';image.dataset.readosImageLoadState='loading';}
          image.src = reference.resourceUrl;
        });
      });
    });
  }
  async function navigateDocumentAnchor(href: string) {
    let id = ''; try { id = decodeURIComponent(href.slice(1)); } catch { return; }
    if (!id) return;
    const coordinator = w.__chatTranscriptScrollCoordinator;
    const intent = coordinator?.createExplicitScrollIntent?.('document_heading');
    let destination = document.getElementById(id);
    const counts = new Map<string, number>();
    const matches = (text: string) => {
      const slug = text.trim().toLowerCase().replace(/[^\p{L}\p{N}_\s-]/gu, '').replace(/\s/g, '-');
      const count = counts.get(slug) || 0; counts.set(slug, count + 1);
      return (count ? `${slug}-${count}` : slug) === id;
    };
    if (!destination) {
      // Use the existing renderer's complete heading index, including nodes
      // outside its mounted viewport. Admission and scrolling remain native.
      for (const mount of document.querySelectorAll<HTMLElement>('#messages .readex-markstream-root')) {
        const host = mount.parentElement, sdk = w.ReadexMarkstreamSDK;
        const heading = sdk?.getRenderBudgetReport?.(host)?.headings?.find((item: any) => matches(String(item.text || '')));
        if (!heading) continue;
        const result = await sdk.ensureNodeForHeading(host, { nodeIndex: heading.nodeIndex });
        if (result?.found && host?.isConnected) destination = host.querySelector(`.node-slot[data-node-index="${heading.nodeIndex}"] :is(h1,h2,h3,h4,h5,h6)`);
        break;
      }
    }
    if (!destination) {
      // Explicit DIL headings need no Markstream node admission.
      for (const heading of document.querySelectorAll<HTMLElement>('#messages :is(h1,h2,h3,h4,h5,h6)')) {
        if (!heading.closest('.readex-markstream-root,.height-estimation-probes') && matches(heading.textContent || '')) { destination = heading; break; }
      }
    }
    if (destination?.isConnected && (!intent || coordinator.resolveExplicitScrollIntent(intent.sequence) === intent))
      coordinator?.scrollConversationToElement(destination, 'top', { allowExplicitNavigation: true, intent });
  }
  async function nativeAction(body: any) {
    if (!body || typeof body !== 'object') return;
    switch (body.action) {
      case 'readosShowResponseAnnotationPopover': w.__readosAnnotationCapsules?.openReference(body); return;
      case 'resolveMarkdownImage': case 'resolveReadexMarkdownImage': {
        const reference = await resolve(String(body.destination || ''));
        const supported=reference?.kind==='image'||reference?.kind==='video';
        w.__readexApplyMarkdownImageSource?.({ requestID: body.requestID, source: supported ? reference.resourceUrl : '', mediaKind: reference?.kind, failed: !supported && body.isFinal === true }); return;
      }
      case 'validateReadexContentReferences': {
        const upgrades: Array<Promise<any>> = [];
        const results = await Promise.all((Array.isArray(body.items) ? body.items.slice(0, 200) : []).map(async (item: any) => {
          const href = String(item.href || ''), url = referenceLinkURL(href);
          if (!url) return { id: item.id, href: item.href, valid: false };
          // A complete tooltip descriptor caches ordinary external links without
          // granting a workspace target or discarding their original time hash.
          if (externalReferenceURL(url)) {
            const video = parseBilibiliReference?.(href);
            if (video) {
              const fallback = { id: item.id, href: item.href, valid: true, ...bilibiliTooltip(video) };
              upgrades.push(bilibiliMetadata(href, video).then(metadata => metadata ? { ...fallback, ...bilibiliTooltip(video, metadata) } : null));
              return fallback;
            }
            return { id: item.id, href: item.href, valid: true, primary: url.hostname, path: href };
          }
          const reference = await resolve(href);
          return reference ? { id: item.id, href: item.href, valid: true, path: reference.target.path, primary: reference.label,
            payload: referencePayload(reference), ...(reference.kind === 'image' ? { imagePreview: { source: reference.resourceUrl } } : {}) }
            : { id: item.id, href: item.href, valid: false };
        }));
        w.__chatTranscriptApplyReadexContentReferenceValidation?.({ requestID: body.requestID, complete: true, results }); scan();
        for (const upgrade of upgrades) void upgrade.then(value => applyBilibiliMetadataUpgrades([value], body.requestID));
        return;
      }
      case 'openReadexContentReference': send('navigate', { payload: body }); return;
      case 'openReadexSubagentPanel': if (typeof body.receiverThreadId === 'string') send('openAgent', { threadId: body.receiverThreadId }); return;
      case 'openReadexSupportPreview': {
        const p = body.preview || {}, payload = p.payload || {};
        if (payload.reados_pdf_extract === true && typeof payload.readosPdfExtractKey === 'string') {
          send('navigate', { payload: { readosPdfExtractKey: payload.readosPdfExtractKey, readosPdfExtractKind: 'extracted' } }); return;
        }
        const cards = Array.from(document.querySelectorAll<HTMLElement>('.readex-image-view-preview'));
        const card = cards.find(card => (card as any).__readexImageViewState?.preview === p)
          || cards.find(card => p.id && (card as any).__readexImageViewState?.preview?.id === p.id);
        const image = card?.querySelector('img');
        const originalSource = previewImageSource(p) || (image ? sourceForImage(image) : '');
        if (image && originalSource) { showImage(originalSource, p.title || p.fileName, image); return; }
        const reference = await resolve(String(p.filePath || p.file_path || payload.output_path || payload.path || ''));
        if (reference?.kind === 'image') showImage(reference.resourceUrl, p.title || p.fileName);
        else if (reference) send('navigate', { payload: referencePayload(reference) });
        return;
      }
      case 'openReadexLocalVideoInlineRange': {
        const reference = await request('resolvePayload', { payload: body });
        const node = document.getElementById(String(body.id || ''));
        if (node && reference) mountVideo(node, reference, body.startSeconds || 0, body.endSeconds); return;
      }
      case 'copyMessage': case 'copyText': case 'copyCode': send('copy', { text: String(body.text || body.content || body.code || '') }); return;
      case 'branchConversation': {
        const presentation = w.__chatTranscriptPresentation;
        if (configuration.document || !transcriptScope || presentation?.forkConversationDisabled || presentation?.forkingMessageID
          || !presentation?.messageActionPolicy?.assistantActions?.includes('branchConversation')) return;
        const message = lastPayload?.messages?.find((message: any) => message.id === body.messageID && message.role === 'assistant');
        if (message && message.status !== 'streaming') send('fork', { scope: transcriptScope, messageID: message.id });
        return;
      }
      case 'syncReadexLocalVideoInlineRanges': case 'showMessageToolbar': return;
      default: if (/^(open|retry|edit|fork|delete|regenerate)/.test(body.action || '')) notice('请使用聊天区域的对应操作。');
    }
  }
  function intelligentAction(body: any) {
    if (!body || typeof body !== 'object') return;
    if (body.type === 'state' || body.type === 'failureOutcome') { send('state', { key: String(body.key || ''), value: body.snapshot ?? body.outcome, stateKind: body.type }); return; }
    if (body.type !== 'action') return;
    if (body.name === 'copy') send('copy', { text: typeof body.value === 'string' ? body.value : String(body.value?.text || '') });
    else if (body.name === 'issueNewTurn') send('followUp', { text: typeof body.value === 'string' ? body.value : String(body.value?.prompt || body.value?.text || '') });
    else if (body.name === 'openUrl') send('openUrl', { url: typeof body.value === 'string' ? body.value : String(body.value?.url || '') });
    else if (body.name === 'openImageLightbox') { if (body.value?.source) showImage(body.value.source, body.value.title); }
    else if (body.name === 'dispatchAction') {
      const value = body.value;
      if (value?.handler === 'client' && value.type === 'open_url') send('openUrl', { url: String(value.payload?.url || '') });
      else notice('此操作尚未接入网页宿主。');
    }
  }
  let selectionAnchor: { id: string; range: Range | null; fallback: { left: number; top: number }; scrollX: number; scrollY: number; active: boolean } | null = null;
  let selectionAnchorFrame = 0;
  function selectionAnchorRect() {
    if (!selectionAnchor) return null;
    const { range, fallback, scrollX, scrollY } = selectionAnchor;
    if (range && !range.commonAncestorContainer.isConnected) return null;
    const rect = range?.getBoundingClientRect() ?? { left: fallback.left + scrollX - window.scrollX, top: fallback.top + scrollY - window.scrollY, width: 1, height: 1 };
    return rect.width > 0 && rect.height > 0 ? { left: rect.left, top: rect.top, width: rect.width, height: rect.height } : null;
  }
  function updateSelectionAnchor() {
    if (!selectionAnchor?.active || selectionAnchorFrame) return;
    selectionAnchorFrame = requestAnimationFrame(() => {
      selectionAnchorFrame = 0;
      if (selectionAnchor?.active) send('selectionAnchor', { id: selectionAnchor.id, rect: selectionAnchorRect() });
    });
  }
  window.addEventListener('scroll', updateSelectionAnchor, true);
  window.addEventListener('resize', updateSelectionAnchor);
  function selectionAction(body: any) {
    if (!body || typeof body !== 'object') return;
    if (body.type === 'copySelection' || body.type === 'copyShortcut') {
      if (typeof body.selectedText === 'string' && body.selectedText.trim()) send('copy', { text: body.selectedText });
      return;
    }
    const action = ({ addToChat: 'add', quickChatDetail: 'detail', askInSideChat: 'side' } as Record<string, string>)[body.type];
    if (!action) return;
    const clean = (value: any) => ({ messageID: value.messageID, selectedText: value.selectedText,
      ...(typeof value.sourceMarkdown === 'string' ? { sourceMarkdown: value.sourceMarkdown } : {}),
      ...(Array.isArray(value.sourceRanges) && value.sourceRanges.length ? { sourceRanges: value.sourceRanges } : {}),
      ...(Array.isArray(value.renderedTextRanges) ? { renderedTextRanges: value.renderedTextRanges } : {}) });
    // Source-base programs stay in the parent. Only the original selection
    // ranges and mapped fragment cross this capability-checked boundary.
    const nativeSelection = window.getSelection?.();
    const range = nativeSelection && !nativeSelection.isCollapsed && nativeSelection.rangeCount ? nativeSelection.getRangeAt(0).cloneRange() : null;
    selectionAnchor = action === 'add' && (range || Number.isFinite(body.clientX) && Number.isFinite(body.clientY))
      ? { id: `selection-${++sequence}`, range, fallback: { left: body.clientX, top: body.clientY }, scrollX: window.scrollX, scrollY: window.scrollY, active: false } : null;
    const rect = selectionAnchorRect();
    send('selectionAction', { action, ...clean(body),
      ...(selectionAnchor && rect ? { anchor: { id: selectionAnchor.id, rect } } : {}),
      ...(Array.isArray(body.selections) ? { selections: body.selections.map(clean) } : {}) });
  }
  const ignore = { postMessage() {} };
  w.webkit = { messageHandlers: {
    messageAction: { postMessage: nativeAction }, readosIntelligentUI: { postMessage: intelligentAction },
    openAttachment: { postMessage: (body: any) => {
      if (!Number.isSafeInteger(body?.attachmentIndex) || body.attachmentIndex < 0) return;
      const message = lastPayload?.messages?.find((message: any) => message.id === body.messageID && message.role === 'user');
      const attachment = message?.attachments?.[body.attachmentIndex];
      if(attachment?.kind==='pdf_outline_context'){showOutlinePreview(message,attachment);return;}
      if(attachment?.kind==='importedImage'&&attachmentImageSource(attachment.url)){
        const article=Array.from(document.querySelectorAll<HTMLElement>('article.message.user')).find(node=>node.dataset.messageId===message.id);
        const image=article?.querySelectorAll<HTMLImageElement>('.message-attachments .attachment')[body.attachmentIndex]?.querySelector<HTMLImageElement>('img');
        showImage(attachment.url,attachment.displayName,image);return;
      }
      if (!attachment || !['workspaceItem','directory'].includes(attachment.kind)) return;
      send('navigate', { payload: { action:'openReadosLibraryAttachment',messageId:message.id,attachmentId:attachment.id } });
    } },
    readosSelectionAction: { postMessage: selectionAction },
    copyCode: { postMessage: (value: any) => send('copy', { text: typeof value === 'string' ? value : String(value?.code || value?.text || '') }) },
    presentationProbe: { postMessage: (body: any) => {
      // The copied coordinator owns the 24px threshold and follows native
      // resize/streaming geometry. Only its small UI state crosses the bridge.
      if (transcriptScope && body?.kind === 'scroll_state' && typeof body.showsScrollToBottomButton === 'boolean')
        send('scrollState', { scope: transcriptScope, canScrollToBottom: body.showsScrollToBottomButton });
    } },
    contentHeight: ignore, selectionChanged: ignore, codeBlockLayoutChanged: ignore,
  } };
  w.__readOSTranscriptPlatform = 'web';
  w.__chatTranscriptSystemSymbols = {};
  w.__readOSTranscriptLocalizationContext = { uiLanguageTag: 'zh-Hans', formatLocaleIdentifier: 'zh-CN', layoutDirection: 'ltr', strings: {}, pluralStrings: {} };
  w.__chatTranscriptPresentation = { ...configuration.presentation,
    messageActionPolicy: { assistantActions: ['copyMessage'], userActions: ['copyMessage'], assistantPlacement: 'readexAssistantFooter' } };
  w.__chatTranscriptSelectionContextMenuOptions = { usesNativeSelectionCopy: true, copyShortcutRequiresMeta: false, usesCodexSelectedTextOverlay: true };
  w.__readosWebBoot = () => {
    booted = true;
    // The copied Bilibili service mounts native WKWebView/AVPlayer overlays.
    // Web keeps these complete URLs in the shared external-link presentation
    // and navigation path, without native media shells or mount requests.
    parseBilibiliReference = w.ReadexBilibiliCitations?.parse;
    w.ReadexBilibiliCitations = undefined;
    const retryVideoMetadata = (event: Event) => {
      if (!event.isTrusted) return;
      const anchor = event.target instanceof Element ? event.target.closest<HTMLAnchorElement>('a[href]') : null;
      const href = anchor?.getAttribute('href') || '', url = referenceLinkURL(href);
      if (!anchor || !externalReferenceURL(url)) return;
      if (event instanceof MouseEvent && event.relatedTarget instanceof Node && anchor.contains(event.relatedTarget)) return;
      const video = parseBilibiliReference?.(href);
      if (!video || anchor.dataset.messageSourceTooltipPrimary !== video.bvid) return;
      void bilibiliMetadata(href, video).then(metadata => {
        if (metadata) applyBilibiliMetadataUpgrades([{ id: anchor.dataset.readexContentReferenceValidationId, href, valid: true, ...bilibiliTooltip(video, metadata) }]);
      });
    };
    document.addEventListener('mouseover', retryVideoMetadata);
    document.addEventListener('focusin', retryVideoMetadata);
    const setImageSource = w.__readexSetImageSource;
    const imageRequests = new WeakMap<HTMLImageElement, string>();
    const unavailableImages = new WeakSet<HTMLImageElement>();
    const unavailableImage = (image: HTMLImageElement) => {
      const card = image.closest('.readex-image-view-preview'), button = image.closest('button');
      if(button&&attachmentImageSource(image.dataset?.webImageSource)) {
        button.classList.add('is-missing-image');
        if(!button.querySelector('.reados-attachment-image-status')) {
          const status=document.createElement('span');status.className='reados-attachment-image-status';
          status.textContent='图片不可用或已更改';button.append(status);
        }
        return;
      }
      if (button) { button.hidden = true; button.disabled = true; button.setAttribute('aria-disabled', 'true'); }
      const status = card?.querySelector<HTMLElement>('.readex-image-view-preview-status');
      if (status) { status.hidden = false; status.textContent = '图片不可用或已删除'; }
    };
    w.__readexSetImageSource = (image: HTMLImageElement, source: string, options: any) => {
      let ownedSource = !!attachmentImageSource(source);
      try {
        const url = new URL(source);
        ownedSource ||= url.origin === configuration.parentOrigin && url.pathname === '/api/artifacts/content'
          && url.searchParams.size === 1 && url.searchParams.has('path')
          && workspaceImageSource(url.searchParams.get('path')) === url.href;
      } catch { /* Existing renderer owns non-workspace sources. */ }
      const apply = (value: string) => typeof setImageSource === 'function' ? setImageSource(image, value, options) : image.src = value;
      if (!ownedSource) { imageRequests.delete(image); unavailableImages.delete(image); return apply(source); }
      const attachmentSource=attachmentImageSource(source);
      if(attachmentSource)image.dataset.webImageSource=attachmentSource;
      if (imageRequests.get(image) === source) { if (unavailableImages.has(image)) unavailableImage(image); return; }
      imageRequests.set(image, source);
      unavailableImages.delete(image);
      void resolve(source).then(reference => {
        if (imageRequests.get(image) !== source) return;
        if (!image.isConnected) { imageRequests.delete(image); return; }
        if (reference?.kind === 'image' && safeMediaURL(reference.resourceUrl)) {
          apply(reference.resourceUrl); return;
        }
        unavailableImages.add(image); unavailableImage(image);
      });
    };
    // Plot snapshots use the same receipt-authorized hydration as raster
    // previews, before setting src; the opaque frame never fetches a raw path.
    w.__readosSetPlotPreviewSource = (image:HTMLImageElement,source:string) => w.__readexSetImageSource(image,source,{});
    w.__chatTranscriptPresentationController?.setConversationPresentation(w.__chatTranscriptPresentation);
    new MutationObserver(records => {
      for (const video of videoDisposers.keys()) if (!video.isConnected) disposeVideo(video);
      for (const frame of pointFrames.keys()) if (!frame.isConnected) disposePointFrame(frame);
      if (records.some(record => record.type !== 'characterData')) scan();
      updateSelectionAnchor();
    }).observe(document.getElementById('messages')!, { childList: true, characterData: true, subtree: true, attributes: true, attributeFilter: ['href', 'src'] });
    // A network error can pause a video before its error event. Retain the last
    // playback intent, but let normal user/range-end pauses clear it.
    for (const type of ['play', 'pause', 'ended']) document.addEventListener(type, event => {
      const video = event.target;
      if (!(video instanceof HTMLVideoElement) || (type === 'pause' && video.error)) return;
      if (type === 'play' && !visible) { video.pause(); return; }
      videoPlayback.set(video, { ...videoPlayback.get(video), playing: type === 'play' });
    }, true);
    // Capture before the original Vue/image lifecycle removes a failed node.
    // Allow one retry per consecutive failure sequence; a decoded resource
    // rearms the budget so long-lived players can renew after later expiries.
    for (const type of ['load', 'loadeddata', 'canplay']) document.addEventListener(type, event => {
      const element = event.target;
      if ((type === 'load' && element instanceof HTMLImageElement && element.naturalWidth > 0)
        || (type !== 'load' && element instanceof HTMLVideoElement && element.readyState >= 2)) mediaRetried.delete(element);
    }, true);
    document.addEventListener('error',event=>{
      const element=event.target;
      if(!(element instanceof HTMLImageElement||element instanceof HTMLVideoElement))return;
      const source=element.getAttribute('src')||'', reference=mediaReferences.get(source);
      if(!reference||mediaRetried.has(element))return;
      event.stopImmediatePropagation();mediaRetried.add(element);
      const playback=element instanceof HTMLVideoElement ? { time:element.currentTime, rate:element.playbackRate, visibilityGeneration:mediaVisibilityGeneration,
        playing:videoPlayback.get(element)?.playing ?? !element.paused, start:videoPlayback.get(element)?.start, end:videoPlayback.get(element)?.end } : null;
      void request('resolve',{href:reference.sourceIdentity||reference.target.path,refresh:true}).then(updated=>{
        if(!element.isConnected||element.getAttribute('src')!==source)return;
        if(!updated||!safeMediaURL(updated.resourceUrl)){element.dispatchEvent(new Event('error'));return;}
        if(element instanceof HTMLVideoElement&&playback) {
          const listeners=new AbortController();
          element.addEventListener('loadedmetadata',()=>{
            const duration=Number.isFinite(element.duration)?Math.max(0,element.duration):Infinity;
            element.currentTime=Math.min(duration,playback.end??duration,Math.max(Math.min(duration,playback.start??0),playback.time));element.playbackRate=playback.rate;
          },{once:true,signal:listeners.signal});
          element.addEventListener('canplay',()=>{
            listeners.abort();
            if(visible&&playback.visibilityGeneration===mediaVisibilityGeneration&&playback.playing&&!element.ended&&(playback.end===undefined||element.currentTime<playback.end))void element.play().catch(()=>{});
          },{once:true,signal:listeners.signal});
          element.addEventListener('error',()=>listeners.abort(),{once:true,signal:listeners.signal});
        }
        const state=(element as any).__readosDilImageLoad;if(state){state.source=updated.resourceUrl;state.status='loading';}
        element.src=updated.resourceUrl;
      });
    },true);
    document.addEventListener('click', event => {
      const target = event.target instanceof Element ? event.target : null;
      const img = target?.closest<HTMLImageElement>('img');
      if (img && safeMediaURL(img.src) && !img.closest('button,dialog')) { event.preventDefault(); event.stopImmediatePropagation(); showImage(img.src, img.alt, img); return; }
      const anchor = target?.closest<HTMLAnchorElement>('a[href]');
      if (anchor && !anchor.closest('.reados-web-lightbox')) {
        event.preventDefault(); event.stopImmediatePropagation();
        const href = anchor.getAttribute('href') || '';
        if (configuration.document && href.startsWith('#') && !/^#L\d+(?:-L?\d+)?$/.test(href)) {
          void navigateDocumentAnchor(href); return;
        }
        send('navigate', { href }); return;
      }
    }, true);
    send('ready');
  };
  window.addEventListener('message', async event => {
    if (event.source !== parent || event.origin !== configuration.parentOrigin) return;
    const data = event.data;
    if (!data || data.channel !== 'reados-renderer-v1' || data.token !== token) return;
    // Other commands keep their original order after the newest queued content.
    if (data.type !== 'render') flushQueuedRender();
    if (data.type === 'theme') {
      if (booted && (transcriptScope === null || data.scope === transcriptScope)
        && typeof data.scope === 'string' && data.scope.length > 0 && data.scope.length <= 4096
        && (data.theme === 'light' || data.theme === 'dark')
        && Object.keys(data).every(key => ['channel', 'token', 'type', 'scope', 'theme'].includes(key))) applyTheme(data.theme);
      return;
    }
    if (data.type === 'visibility') {
      if (booted && data.scope === transcriptScope && typeof data.visible === 'boolean'
        && Object.keys(data).every(key => ['channel', 'token', 'type', 'scope', 'visible'].includes(key))) applyVisibility(data.visible);
      return;
    }
    if (data.type === 'watchSelectionAnchor') {
      if (selectionAnchor && selectionAnchor.id === data.id && typeof data.active === 'boolean'
        && Object.keys(data).every(key => ['channel', 'token', 'type', 'id', 'active'].includes(key))) {
        selectionAnchor.active = data.active;
        updateSelectionAnchor();
      }
      return;
    }
    if (data.type === 'scrollToBottom') {
      if (!booted || !transcriptScope || data.scope !== transcriptScope
        || !Number.isSafeInteger(data.request) || data.request < 0 || data.request <= lastScrollRequest
        || Object.keys(data).some(key => !['channel', 'token', 'type', 'scope', 'request'].includes(key))) return;
      lastScrollRequest = data.request;
      // Keep explicit navigation, cancellation, animation and live-edge
      // reattachment under the original scroll coordinator's ownership.
      w.__chatTranscriptCommandBridge.execute('scroll_to_bottom');
      return;
    }
    if (data.type === 'turnNavigationPointerLeave') {
      if (booted && turnNavigationScope && data.scope === turnNavigationScope
        && Object.keys(data).every(key => ['channel', 'token', 'type', 'scope'].includes(key)))
        w.__readosTurnNavigation?.dismissPointer();
      return;
    }
    if (data.type === 'imageViewerClosed' && typeof data.requestId === 'string') {
      const restore = imageViewerReturn;
      if (restore && restore.requestId === data.requestId) {
        imageViewerReturn = null;
        window.scrollTo(restore.x, restore.y);
        for (const item of restore.scroll) { item.node.scrollLeft = item.left; item.node.scrollTop = item.top; }
        restore.focus?.focus({ preventScroll: true });
      }
      return;
    }
    if (data.type === 'asyncQuestionResult') { w.__readosAsyncQuestions?.result(data); return; }
    if (data.type === 'response' && typeof data.id === 'string') {
      const done = pending.get(data.id);
      if (done) {
        pending.delete(data.id);
        if(data.value?.resourceUrl&&safeMediaURL(data.value.resourceUrl)) {
          mediaReferences.set(data.value.resourceUrl,data.value);
          while(mediaReferences.size>128)mediaReferences.delete(mediaReferences.keys().next().value!);
        }
        done(data.value);
      }
      return;
    }
    if (data.type !== 'render' || !booted || typeof data.scope !== 'string' || !data.scope || data.scope.length > 4096
      || !data.payload || !Array.isArray(data.payload.messages) || !Array.isArray(data.payload.blockCatalog)) return;
    const firstScope = transcriptScope === null;
    const firstRender = !hasCommittedRender;
    // The parent remounts for a different scope; reject delayed render posts
    // from a previous conversation instead of mixing its scroll ownership.
    if (!firstScope && transcriptScope !== data.scope) return;
    if (firstScope) initialScrollOwnershipGeneration = Number(w.__chatTranscriptUIState?.userScrollOwnershipGeneration) || 0;
    transcriptScope = data.scope;
    w.__readosWebVisualizations?.scope(data.scope);
    const fork = data.forkControls;
    forkControls = { enabled: !configuration.document && !configuration.readOnly && fork?.enabled === true, disabled: fork?.disabled === true,
      messageID: typeof fork?.messageID === 'string' ? fork.messageID : null };
    if (typeof data.resourceVersion === 'string' && resourceVersion !== data.resourceVersion) {
      resourceVersion = data.resourceVersion; referenceCache.clear();
      document.querySelectorAll<HTMLElement>('#messages a[data-web-reference-href]').forEach(anchor => { delete anchor.dataset.webReferenceHref; });
    }
    visible = data.visible !== false;
    const currentRenderSequence = ++renderSequence;
    turnNavigationSequence++;
    turnNavigationScope = typeof data.scope === 'string' ? data.scope : '';
    deferredRender = null;
    if (renderRetryTimer !== null) { clearTimeout(renderRetryTimer); renderRetryTimer = null; }
    if (renderBurstOpen) { queuedRender = { data, firstRender, sequence: currentRenderSequence }; return; }
    openRenderBurst();
    await renderPayload(data, firstRender, currentRenderSequence);
  });
  async function renderPayload(data: any, firstRender: boolean, currentRenderSequence: number) {
    const currentTurnNavigationSequence = turnNavigationSequence;
    try {
      // A deferred content publication may have been queued before a newer
      // appearance command. The current host theme remains authoritative.
      data = { ...data, payload: { ...data.payload, theme } };
      const previousSkip = w.__chatTranscriptSkipPresentationRerender;
      w.__chatTranscriptSkipPresentationRerender = true;
      try {
        w.__chatTranscriptPresentationController?.setConversationPresentation({ ...w.__chatTranscriptPresentation,
          forkConversationDisabled: forkControls.disabled, forkingMessageID: forkControls.messageID,
          messageActionPolicy: { assistantActions: forkControls.enabled ? ['copyMessage', 'branchConversation'] : ['copyMessage'],
            userActions: ['copyMessage'], assistantPlacement: 'readexAssistantFooter' },
          isConversationGenerating: data.payload.messages.some((message: any) => message.status === 'streaming') });
      } finally { w.__chatTranscriptSkipPresentationRerender = previousSkip; }
      if (data.dil) configurationDIL = data.dil;
      if (configurationDIL) {
        const turns = data.payload.messages.filter((m: any) => m.role === 'assistant').map((m: any) => m.readexCodexTurnID);
        activeDILConfiguration = { ...configurationDIL, turnIDs: turns, scope: data.scope, visible, theme, locale: 'zh-CN', codeExecutionLanguages: [], imageQuerySupport: false };
        w.ReadOSIntelligentUI.configure({ ...activeDILConfiguration, states: data.states || {} });
      }
      lastPayload = data.payload;
      w.__readosAsyncQuestions?.configure(data.payload, data.scope,
        { enabled: !configuration.document && data.asyncQuestionControls?.enabled === true, disabled: data.asyncQuestionControls?.disabled === true });
      if(outlinePreview){
        const current=lastPayload.messages.find((message:any)=>message.id===outlinePreview!.messageId&&message.role==='user')
          ?.attachments?.find((attachment:any)=>attachment.id===outlinePreview!.attachmentId);
        if(!current||current.kind!=='pdf_outline_context'||current.markdown!==outlinePreview.markdown)outlinePreview.dialog.close();
      }
      const previousCommits = w.__chatTranscriptCurrentDOMCommitPromises;
      const commits: Promise<unknown>[] = [];
      w.__chatTranscriptCurrentDOMCommitPromises = commits;
      let mutation: unknown;
      try {
        mutation = w.__chatTranscriptCommandBridge.execute('render_payload', lastPayload, { followBottomIfNearBottom: !configuration.document, forceImmediateRender: true,
          ...(firstRender && initialScrollOwnershipGeneration === (Number(w.__chatTranscriptUIState?.userScrollOwnershipGeneration) || 0)
            ? { initialScrollTarget: configuration.document ? 'top' : 'bottom' } : {}) });
      } finally {
        if (previousCommits === undefined) delete w.__chatTranscriptCurrentDOMCommitPromises;
        else w.__chatTranscriptCurrentDOMCommitPromises = previousCommits;
      }
      const result = await mutation as { renderDeferred?: boolean; viewportMutationRejected?: boolean; mutationRejected?: boolean } | undefined;
      if (currentRenderSequence !== renderSequence) return;
      if (result?.renderDeferred === true || result?.viewportMutationRejected === true || result?.mutationRejected === true) {
        scheduleRenderRetry(data, firstRender, currentRenderSequence);
        return;
      }
      await Promise.all(commits);
      if (currentRenderSequence !== renderSequence) return;
      w.ReadOSIntelligentUI?.refreshMounted();
      w.ReadOSInlineVisualizations?.refresh();
      if (!visible) applyVisibility(false);
      w.__chatTranscriptScrollCoordinator?.postScrollState('web_render', { force: true });
      if (currentTurnNavigationSequence === turnNavigationSequence && lastPayload === data.payload && data.scope === turnNavigationScope)
        w.__readosTurnNavigation?.update(data.payload, data.scope);
      hasCommittedRender = true;
      scan(); updateSelectionAnchor(); send('rendered', { messageCount: data.payload.messages.length });
    } catch (error) {
      if (currentRenderSequence === renderSequence)
        send('error', { message: error instanceof Error ? error.message : String(error) });
    }
  }
  window.addEventListener('error', e => {
    // Browsers report an observer delivery overflow as a window error even
    // though no script exception occurred. It is expected while a pane is
    // being resized and must not replace the transcript with a red error bar.
    if (e.error == null && (e.message === 'ResizeObserver loop completed with undelivered notifications.'
      || e.message === 'ResizeObserver loop limit exceeded')) return;
    send('error', { message: e.message });
  });
}
